import axios, { type AxiosAdapter, type AxiosError, type AxiosInstance } from "axios";
import type { InternalHttpRequestConfig } from "./types";

const MAX_REPLAY_ATTEMPTS = 1;

interface SessionPort {
  getSessionVersion(): number;
  isSameSession(version: number | undefined): boolean;
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  refresh(): Promise<string>;
}

interface FeedbackPort {
  onSuccess: (response: import("axios").AxiosResponse) => void;
  onError: (error: AxiosError) => void;
}

interface TransportOptions {
  baseURL: string;
  adapter?: AxiosAdapter;
  session: SessionPort;
  feedback: FeedbackPort;
}

export function createHttpTransport({
  baseURL,
  adapter,
  session,
  feedback,
}: TransportOptions): AxiosInstance {
  const options = {
    baseURL,
    timeout: 60000,
    adapter,
    headers: { "Content-Type": "application/json" },
  };
  const transport = axios.create(options);

  transport.interceptors.request.use((config: InternalHttpRequestConfig) => {
    if (/^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(config.url ?? "")) {
      throw new Error("HttpClient aceita apenas caminhos relativos da API.");
    }
    config._sessionVersion ??= session.getSessionVersion();
    const token = session.getAccessToken();
    const isPublicRequest = config.authMode === "none" || config.url?.startsWith("/api/v1/auth/");
    if (token && !isPublicRequest) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  });

  transport.interceptors.response.use(
    (response) => {
      feedback.onSuccess(response);
      return response;
    },
    async (error: AxiosError) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);
      if (error.code === "ERR_CANCELED") return Promise.reject(error);
      const request = error.config as InternalHttpRequestConfig | undefined;
      if (request?.url === "/api/v1/auth/refresh") return Promise.reject(error);

      if (
        error.response?.status === 401 &&
        request?.authMode !== "none" &&
        request?.url !== "/api/v1/auth/login" &&
        request
      ) {
        if (!session.isSameSession(request._sessionVersion)) return Promise.reject(error);
        request._retryCount ??= 0;
        if (request._retryCount < MAX_REPLAY_ATTEMPTS) {
          request._retryCount += 1;
          const currentToken = session.getAccessToken();
          if (currentToken && request.headers.Authorization !== `Bearer ${currentToken}`) {
            return transport.request(request);
          }
          if (session.getRefreshToken()) {
            try {
              await session.refresh();
            } catch {
              if (!session.isSameSession(request._sessionVersion)) return Promise.reject(error);
              feedback.onError(error);
              return Promise.reject(error);
            }
            return transport.request(request);
          }
        }
      }

      feedback.onError(error);
      return Promise.reject(error);
    },
  );

  return transport;
}
